import React, { useEffect, useState } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { User as UserIcon, Mail, Calendar, ShieldCheck, KeyRound, Save, Crown, Trash2, RotateCcw, Clock, CheckCircle2, XCircle, LogOut, AlertTriangle, X } from 'lucide-react';
import { useLang, tx } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const StatusPill = ({ status, lang }) => {
  const map = {
    pending: { cls: 'bg-amber-500/20 text-amber-700 border-amber-500/40', label: tx(lang, 'قيد الانتظار', 'Pending'), Icon: Clock },
    approved: { cls: 'bg-green-500/20 text-green-700 border-green-500/40', label: tx(lang, 'مقبول', 'Approved'), Icon: CheckCircle2 },
    rejected: { cls: 'bg-red-500/20 text-red-700 border-red-500/40', label: tx(lang, 'مرفوض', 'Rejected'), Icon: XCircle },
  };
  const cur = map[status] || map.pending;
  const I = cur.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-bold ${cur.cls}`}>
      <I className="w-3.5 h-3.5" /> {cur.label}
    </span>
  );
};

const Section = ({ title, icon: Icon, children, className = '' }) => (
  <div className={`rounded-2xl bg-white border border-slate-200 p-6 ${className}`}>
    <div className="flex items-center gap-3 mb-5">
      <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-blue-600" />
      </div>
      <h2 className="text-slate-900 font-extrabold text-lg md:text-xl">{title}</h2>
    </div>
    {children}
  </div>
);

const ConfirmModal = ({ open, onClose, onConfirm, title, message, confirmText, danger, lang }) => {
  if (!open) return null;
  return (
    <div onClick={onClose} className="fixed inset-0 z-[100] bg-black/70 backdrop-blur flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-white border-2 border-red-500/40 p-6 shadow-2xl">
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${danger ? 'bg-red-500/20' : 'bg-amber-500/20'}`}>
            <AlertTriangle className={`w-6 h-6 ${danger ? 'text-red-600' : 'text-amber-600'}`} />
          </div>
          <div className="flex-1">
            <h3 className="text-slate-900 font-bold text-lg mb-2">{title}</h3>
            <p className="text-slate-600 text-sm">{message}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-2 justify-end">
          <Button onClick={onClose} variant="outline" className="border-blue-200 text-slate-700">
            {tx(lang, 'إلغاء', 'Cancel')}
          </Button>
          <Button onClick={onConfirm} className={`border-0 ${danger ? 'bg-red-600 hover:bg-red-700 text-white' : 'btn-primary-blue'}`} data-testid="confirm-action-btn">
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
};

const Profile = () => {
  const { lang, isRTL } = useLang();
  const { user, loading, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const [curPwd, setCurPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [savingPwd, setSavingPwd] = useState(false);

  const [reqType, setReqType] = useState(null); // 'refund' | 'delete'
  const [reqReason, setReqReason] = useState('');
  const [submittingReq, setSubmittingReq] = useState(false);
  const [confirmReq, setConfirmReq] = useState(false);

  const [requests, setRequests] = useState([]);
  const [confirmCancel, setConfirmCancel] = useState(null);

  useEffect(() => {
    if (user) setName(user.name || '');
  }, [user]);

  const loadRequests = async () => {
    try {
      const { data } = await api.get('/account/requests');
      setRequests(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  };

  useEffect(() => { if (user) loadRequests(); }, [user]);

  if (loading) {
    return <main className="pt-28 min-h-screen text-center text-slate-500">{tx(lang, 'جارٍ التحميل...', 'Loading...')}</main>;
  }
  if (!user) return <Navigate to="/login" replace />;

  const fmtDate = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleDateString(tx(lang, 'ar-EG', 'en-US'), { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return iso; }
  };

  const sub = user.subscription || {};
  const hasSub = sub.status === 'active' && sub.expires_at && new Date(sub.expires_at) > new Date();

  const cancelPaypal = async () => {
    if (!window.confirm(tx(lang, 'إلغاء التجديد التلقائي؟ ستبقى مشتركاً حتى نهاية الفترة المدفوعة ولن يتم سحب أي مبلغ بعدها.', 'Cancel auto-renew? You keep access until the end of the paid period and will not be charged again.'))) return;
    setCancelling(true);
    try {
      await api.post(sub.provider === 'stripe' ? '/stripe/subscription/cancel' : '/paypal/subscriptions/cancel');
      await refreshUser();
      toast.success(tx(lang, 'تم إلغاء التجديد التلقائي', 'Auto-renew cancelled'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally { setCancelling(false); }
  };

  const resumeStripe = async () => {
    setCancelling(true);
    try {
      await api.post('/stripe/subscription/resume');
      await refreshUser();
      toast.success(tx(lang, 'تم تفعيل التجديد التلقائي', 'Auto-renew turned back on'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally { setCancelling(false); }
  };
  const recurring = ['paypal', 'stripe'].includes(sub.provider) && (sub.provider === 'paypal' || sub.stripe_subscription_id);

  const saveName = async (e) => {
    e.preventDefault();
    if (!name.trim() || name.trim() === user.name) return;
    setSavingName(true);
    try {
      await api.patch('/auth/me', { name: name.trim() });
      await refreshUser();
      toast.success(tx(lang, 'تم تحديث الاسم', 'Name updated'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally { setSavingName(false); }
  };

  const savePwd = async (e) => {
    e.preventDefault();
    if (!curPwd || !newPwd) return;
    setSavingPwd(true);
    try {
      await api.patch('/auth/me/password', { current_password: curPwd, new_password: newPwd });
      toast.success(tx(lang, 'تم تغيير كلمة المرور', 'Password changed'));
      setCurPwd(''); setNewPwd('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally { setSavingPwd(false); }
  };

  const submitRequest = async () => {
    setConfirmReq(false);
    setSubmittingReq(true);
    try {
      await api.post('/account/requests', { type: reqType, reason: reqReason });
      toast.success(tx(lang, 'تم إرسال الطلب بنجاح. سيتم مراجعته من الإدارة.', 'Request submitted. Admin will review it.'));
      setReqType(null); setReqReason('');
      await loadRequests();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally { setSubmittingReq(false); }
  };

  const cancelRequest = async () => {
    const id = confirmCancel; setConfirmCancel(null);
    try {
      await api.delete(`/account/requests/${id}`);
      toast.success(tx(lang, 'تم إلغاء الطلب', 'Request cancelled'));
      await loadRequests();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const onLogout = async () => { await logout(); navigate('/'); };

  const initials = (user.name || user.email || '?').trim().charAt(0).toUpperCase();

  return (
    <main className="page-y min-h-screen" data-testid="profile-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header card */}
        <div className="rounded-3xl bg-gradient-to-br from-blue-50 via-white to-slate-50 border border-blue-200 p-6 md:p-8 mb-6">
          <div className={`flex flex-col sm:flex-row items-center gap-5 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-24 h-24 rounded-2xl object-cover border-2 border-blue-200 shadow-xl" />
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 border-2 border-blue-200 flex items-center justify-center text-white text-4xl font-extrabold shadow-xl">
                {initials}
              </div>
            )}
            <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1.5" dir={isRTL ? 'rtl' : 'ltr'}>
                <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">{user.name}</h1>
                {user.role === 'admin' && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-blue-100 border border-blue-200 text-blue-700 text-xs font-bold">
                    <ShieldCheck className="w-3 h-3" /> Admin
                  </span>
                )}
                {hasSub && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-700 text-xs font-bold">
                    <Crown className="w-3 h-3" /> {tx(lang, 'مشترك', 'Subscribed')}
                  </span>
                )}
              </div>
              <div className="text-slate-500 text-sm flex items-center gap-1.5" dir="ltr"><Mail className="w-4 h-4" /> {user.email}</div>
            </div>
            <Button onClick={onLogout} variant="outline" className="border-red-500/40 text-red-700 hover:bg-red-900/30" data-testid="profile-logout-btn">
              <LogOut className="w-4 h-4 me-2" /> {tx(lang, 'تسجيل الخروج', 'Logout')}
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          {/* Edit Name */}
          <Section title={tx(lang, 'المعلومات الشخصية', 'Personal Info')} icon={UserIcon}>
            <form onSubmit={saveName} className="space-y-3">
              <label className="text-slate-600 text-sm">{tx(lang, 'الاسم', 'Name')}</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} required className="bg-slate-50 border-slate-200 text-slate-900" data-testid="profile-name-input" />
              <Button type="submit" disabled={savingName || !name.trim() || name.trim() === user.name} className="btn-primary-blue border-0" data-testid="save-name-btn">
                <Save className="w-4 h-4 me-2" /> {savingName ? '...' : (tx(lang, 'حفظ', 'Save'))}
              </Button>
            </form>
          </Section>

          {/* Change Password */}
          <Section title={tx(lang, 'تغيير كلمة المرور', 'Change Password')} icon={KeyRound}>
            <form onSubmit={savePwd} className="space-y-3">
              <Input type="password" value={curPwd} onChange={(e) => setCurPwd(e.target.value)} placeholder={tx(lang, 'كلمة المرور الحالية', 'Current password')} required className="bg-slate-50 border-slate-200 text-slate-900" data-testid="current-pwd-input" />
              <Input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder={tx(lang, 'كلمة المرور الجديدة (٦ أحرف على الأقل)', 'New password (6+ chars)')} minLength={6} required className="bg-slate-50 border-slate-200 text-slate-900" data-testid="new-pwd-input" />
              <Button type="submit" disabled={savingPwd || !curPwd || newPwd.length < 6} className="btn-primary-blue border-0" data-testid="save-pwd-btn">
                <Save className="w-4 h-4 me-2" /> {savingPwd ? '...' : (tx(lang, 'تحديث', 'Update'))}
              </Button>
            </form>
          </Section>

          {/* Subscription */}
          <Section title={tx(lang, 'الاشتراك', 'Subscription')} icon={Crown} className="lg:col-span-2">
            {hasSub ? (
              <div className={`flex flex-wrap items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="px-4 py-2 rounded-xl bg-amber-500/10 border border-amber-500/30">
                  <div className="text-amber-700 font-bold text-sm">{tx(lang, 'الباقة', 'Plan')}</div>
                  <div className="text-slate-900 font-extrabold" data-testid="profile-sub-plan">{(lang === 'en' && sub.plan_nameEn) || sub.plan_name || sub.plan_id || '-'}</div>
                </div>
                <div className="px-4 py-2 rounded-xl bg-blue-50 border border-blue-200">
                  <div className="text-blue-700 font-bold text-sm flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{recurring && sub.auto_renew !== false ? tx(lang, 'التجديد القادم', 'Next renewal') : tx(lang, 'تنتهي في', 'Expires')}</div>
                  <div className="text-slate-900 font-extrabold" data-testid="profile-sub-expires">{fmtDate(sub.renews_at || sub.expires_at)}</div>
                </div>
                <div className="px-4 py-2 rounded-xl bg-green-500/10 border border-green-500/30">
                  <div className="text-green-700 font-bold text-sm">{tx(lang, 'الحالة', 'Status')}</div>
                  <div className="text-slate-900 font-extrabold" data-testid="profile-sub-status">
                    {sub.payment_failed ? tx(lang, 'فشل الدفع — حدّث بطاقتك', 'Payment failed — update your card')
                      : recurring && sub.auto_renew === false ? tx(lang, 'نشط (لن يتجدد)', 'Active (will not renew)')
                      : recurring ? tx(lang, 'نشط (يتجدد تلقائياً)', 'Active (auto-renews)') : tx(lang, 'نشط', 'Active')}
                  </div>
                </div>
                {recurring && sub.auto_renew !== false && (
                  <Button data-testid="cancel-renewal-btn" onClick={cancelPaypal} disabled={cancelling} variant="outline" className="border-red-500/40 text-red-600 hover:bg-red-500/10 bg-transparent">
                    {cancelling ? '...' : (tx(lang, 'إلغاء التجديد', 'Cancel renewal'))}
                  </Button>
                )}
                {sub.provider === 'stripe' && sub.stripe_subscription_id && sub.auto_renew === false && (
                  <Button data-testid="resume-stripe-sub-btn" onClick={resumeStripe} disabled={cancelling} variant="outline" className="border-blue-500/40 text-blue-700 hover:bg-blue-500/10 bg-transparent">
                    {cancelling ? '...' : (tx(lang, 'إعادة تفعيل التجديد', 'Resume renewal'))}
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-slate-600 mb-3">{tx(lang, 'لا يوجد اشتراك نشط حالياً', 'No active subscription')}</p>
                <Link to="/subscription" data-testid="profile-subscribe-btn" className="btn-subscribe h-10 px-5"><Crown className="w-4 h-4" />{tx(lang, 'اشترك الآن', 'Subscribe now')}</Link>
              </div>
            )}
          </Section>

          {/* Account Actions */}
          <Section title={tx(lang, 'طلبات الحساب', 'Account Requests')} icon={AlertTriangle} className="lg:col-span-2">
            {!reqType ? (
              <div className="grid sm:grid-cols-2 gap-3">
                <button onClick={() => { setReqType('refund'); setReqReason(''); }} disabled={!hasSub} data-testid="request-refund-btn" className="text-start p-4 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  <div className="flex items-center gap-2 text-amber-700 font-bold mb-1"><RotateCcw className="w-5 h-5" />{tx(lang, 'طلب استرداد الأموال', 'Refund Request')}</div>
                  <div className="text-slate-500 text-xs">{hasSub ? (tx(lang, 'سيتم مراجعة طلبك خلال 24-48 ساعة', 'Reviewed within 24-48 hours')) : (tx(lang, 'يجب أن يكون لديك اشتراك نشط', 'Active subscription required'))}</div>
                </button>
                <button onClick={() => { setReqType('delete'); setReqReason(''); }} data-testid="request-delete-btn" className="text-start p-4 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 transition-colors">
                  <div className="flex items-center gap-2 text-red-700 font-bold mb-1"><Trash2 className="w-5 h-5" />{tx(lang, 'طلب حذف الحساب', 'Delete Account')}</div>
                  <div className="text-slate-500 text-xs">{tx(lang, 'حذف نهائي وغير قابل للاسترجاع', 'Permanent and irreversible')}</div>
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className={`px-3 py-2 rounded-lg ${reqType === 'delete' ? 'bg-red-500/10 border border-red-500/30 text-red-700' : 'bg-amber-500/10 border border-amber-500/30 text-amber-700'} text-sm font-bold`}>
                  {reqType === 'delete' ? (tx(lang, '⚠️ سيتم حذف حسابك نهائياً عند الموافقة', '⚠️ Account will be permanently deleted upon approval')) : (tx(lang, 'طلب استرداد الأموال', 'Refund Request'))}
                </div>
                <Textarea
                  value={reqReason}
                  onChange={(e) => setReqReason(e.target.value)}
                  rows={4}
                  maxLength={1000}
                  placeholder={tx(lang, 'اذكر سبب طلبك (اختياري)...', 'Reason for your request (optional)...')}
                  className="bg-slate-50 border-slate-200 text-slate-900"
                  data-testid="request-reason-input"
                />
                <div className="flex gap-2">
                  <Button onClick={() => setConfirmReq(true)} disabled={submittingReq} className={`border-0 ${reqType === 'delete' ? 'bg-red-600 hover:bg-red-700 text-white' : 'btn-primary-blue'}`} data-testid="submit-request-btn">
                    {submittingReq ? '...' : (tx(lang, 'إرسال الطلب', 'Submit Request'))}
                  </Button>
                  <Button onClick={() => setReqType(null)} variant="outline" className="border-blue-200 text-slate-700">
                    {tx(lang, 'إلغاء', 'Cancel')}
                  </Button>
                </div>
              </div>
            )}

            {/* Past requests */}
            {requests.length > 0 && (
              <div className="mt-6">
                <h3 className="text-slate-700 font-bold mb-3 text-sm">{tx(lang, 'سجل الطلبات', 'Request History')}</h3>
                <div className="space-y-2">
                  {requests.map(r => (
                    <div key={r.id} className="rounded-xl bg-slate-50 border border-slate-200 p-3" data-testid={`request-row-${r.id}`}>
                      <div className={`flex flex-wrap items-center justify-between gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${r.type === 'delete' ? 'bg-red-500/20 text-red-700' : 'bg-amber-500/20 text-amber-700'}`}>
                            {r.type === 'delete' ? (tx(lang, 'حذف حساب', 'Account Delete')) : (tx(lang, 'استرداد', 'Refund'))}
                          </span>
                          <StatusPill status={r.status} lang={lang} />
                          <span className="text-slate-500 text-xs">{fmtDate(r.created_at)}</span>
                        </div>
                        {r.status === 'pending' && (
                          <button onClick={() => setConfirmCancel(r.id)} className="text-red-600 hover:text-red-700 text-xs underline" data-testid={`cancel-request-${r.id}`}>
                            {tx(lang, 'إلغاء', 'Cancel')}
                          </button>
                        )}
                      </div>
                      {r.reason && <div className="mt-2 text-slate-600 text-sm">{r.reason}</div>}
                      {r.admin_note && <div className="mt-2 text-blue-700 text-xs italic">{tx(lang, 'رد الإدارة:', 'Admin reply:')} {r.admin_note}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Section>
        </div>
      </div>

      <ConfirmModal
        open={confirmReq}
        onClose={() => setConfirmReq(false)}
        onConfirm={submitRequest}
        title={reqType === 'delete' ? (tx(lang, 'تأكيد طلب حذف الحساب', 'Confirm Account Deletion')) : (tx(lang, 'تأكيد طلب الاسترداد', 'Confirm Refund'))}
        message={reqType === 'delete' ? (tx(lang, 'بعد موافقة الإدارة، سيتم حذف حسابك وكل بياناتك نهائياً ولا يمكن استعادتها.', 'Upon admin approval, your account and all data will be permanently deleted.')) : (tx(lang, 'سيتم مراجعة طلبك من قِبل الإدارة. هل أنت متأكد؟', 'Your request will be reviewed by admin. Are you sure?'))}
        confirmText={tx(lang, 'نعم، إرسال الطلب', 'Yes, submit')}
        danger={reqType === 'delete'}
        lang={lang}
      />

      <ConfirmModal
        open={!!confirmCancel}
        onClose={() => setConfirmCancel(null)}
        onConfirm={cancelRequest}
        title={tx(lang, 'إلغاء الطلب؟', 'Cancel request?')}
        message={tx(lang, 'هل تريد إلغاء هذا الطلب؟', 'Do you want to cancel this request?')}
        confirmText={tx(lang, 'نعم، إلغاء', 'Yes, cancel')}
        danger={false}
        lang={lang}
      />
    </main>
  );
};

export default Profile;
